import { apiClient } from './client';
import type { DataSource } from '../types';

export interface CreateDataSourceRequest {
  name: string;
  sourceType: string;
  sourceName: string;
  parameters?: Record<string, ParameterDefinition>;
  schedule?: ScheduleConfig;
}

export interface UpdateDataSourceRequest {
  name?: string;
  sourceType?: string;
  sourceName?: string;
  parameters?: Record<string, ParameterDefinition>;
  schedule?: ScheduleConfig;
  isActive?: boolean;
}

export interface ParameterDefinition {
  type: string;
  defaultValue?: string;
  required: boolean;
}

export interface ScheduleConfig {
  type: string;
  value: string;
}

export interface SyncHistory {
  id: number;
  sourceId: number;
  startedAt: string;
  completedAt: string | null;
  status: string;
  rowsFetched: number;
  errorMessage: string | null;
}

export interface BulkImportItem {
  name: string;
  sourceName: string;
  sourceType?: string;
  parameters?: Record<string, ParameterDefinition>;
  schedule?: ScheduleConfig;
}

export interface BulkImportError {
  index: number;
  sourceName: string;
  reason: string;
}

export interface BulkImportResult {
  imported: number;
  updated: number;
  skipped: number;
  errors: BulkImportError[];
}

export const dataSourcesApi = {
  // Get all data sources for a firm
  getByFirm: async (firmId: number): Promise<DataSource[]> => {
    const response = await apiClient.get(`/api/firms/${firmId}/data-sources`);
    return response.data;
  },

  // Get data source by ID
  getById: async (firmId: number, id: number): Promise<DataSource> => {
    const response = await apiClient.get(`/api/firms/${firmId}/data-sources/${id}`);
    return response.data;
  },

  // Create new data source
  create: async (firmId: number, data: CreateDataSourceRequest): Promise<DataSource> => {
    const response = await apiClient.post(`/api/firms/${firmId}/data-sources`, data);
    return response.data;
  },

  // Update data source
  update: async (firmId: number, id: number, data: UpdateDataSourceRequest): Promise<DataSource> => {
    const response = await apiClient.put(`/api/firms/${firmId}/data-sources/${id}`, data);
    return response.data;
  },

  // Delete data source
  delete: async (firmId: number, id: number): Promise<void> => {
    await apiClient.delete(`/api/firms/${firmId}/data-sources/${id}`);
  },

  // Trigger manual sync
  triggerSync: async (firmId: number, id: number): Promise<{ message: string; agentId: number }> => {
    const response = await apiClient.post(`/api/firms/${firmId}/data-sources/${id}/sync`);
    return response.data;
  },

  // Get sync history
  getSyncHistory: async (firmId: number, id: number): Promise<SyncHistory[]> => {
    const response = await apiClient.get(`/api/firms/${firmId}/data-sources/${id}/history`);
    return response.data;
  },

  // Bulk import — used to seed/sync DataSource rows from ittihat-side template list.
  // Idempotent UPSERT keyed on sourceName.
  // [Deprecated] Replaced by templateMirror.forceSync; kept as emergency escape hatch.
  bulkImport: async (firmId: number, items: BulkImportItem[]): Promise<BulkImportResult> => {
    const response = await apiClient.post(`/api/firms/${firmId}/data-sources/import`, { items });
    return response.data;
  },
};

export interface TemplateSyncResponse {
  status:
    | 'Synced'
    | 'NotModified'
    | 'FirmMissing'
    | 'FirmInactive'
    | 'AuthFailed'
    | 'TransientError'
    | 'SkippedNotIttihatBackend';
  templatesUpserted: number;
  orphansDemoted: number;
  eTag: string | null;
  lastSyncedAt: string | null;
  error: string | null;
}

export const templateMirrorApi = {
  // Force-pull the ittihat hub template catalog for one firm and reconcile the mirror.
  // Bypasses the ETag conditional GET so the admin always gets a fresh 200.
  forceSync: async (firmId: number): Promise<TemplateSyncResponse> => {
    const response = await apiClient.post<TemplateSyncResponse>(
      `/api/admin/firms/${firmId}/template-sync`,
    );
    return response.data;
  },
};
